import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  TextField,
  Chip,
  TableSortLabel,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PublicIcon from '@mui/icons-material/Public';
import PaidIcon from '@mui/icons-material/Paid';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AllBreedSubmitDialog from '../components/AllBreedSubmitDialog';
import PedigreeModal from '../components/PedigreeModal';
import SaleAgreementDialog from '../components/SaleAgreementDialog';
import { horsesAPI, incomeAPI, expensesAPI, referenceAPI } from '../services/api';
import { formatDate, formatUtcDateTime } from '../utils/dateFormat';
import { formatHorseCurrentStatus, ALL_HORSE_STATUSES } from '../utils/horseStatus';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { calculateHorseAge } from '../utils/horseAge';
import { buildSaleIncomeSummary, buildPurchaseExpenseSummary, formatSaleAmount } from '../utils/horseSaleIncome';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { toArray } from '../utils/apiData';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { useNavigate } from 'react-router-dom';
import { useHorseModal } from '../context/HorseModalContext';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

// Width of the sticky Actions column (a single kebab button). Name offsets from
// this, so keep them in sync when it changes. Only these two columns are pinned —
// anything more and the frozen block is wider than a portrait phone, leaving the
// scrolling columns nowhere to appear.
const ACTIONS_COLUMN_WIDTH = 56;

// Statuses where summing Sale Amount is meaningful. For a horse still on the market the
// figure is an asking price; once sold it is what the horse actually brought, so the label
// changes with the filter rather than reading a generic "total" for both.
const SALE_TOTAL_LABELS = {
  for_sale: 'Total asking price',
  sale_pending: 'Total pending sale amount',
  sold: 'Total sale amount',
};

// Barn name column: sized for ~10 characters (10ch at the 0.875rem body font)
// plus the cell's 8px left / 16px right padding. Longer names ellipsize.
const NAME_COLUMN_WIDTH = 104;

const FOALED_COLUMN_WIDTH = 120;

const OWNER_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All Owner Types' },
  { value: 'owned', label: 'Owned' },
  { value: 'leased', label: 'Leased' },
  { value: 'client', label: 'Client' },
  { value: 'boarding', label: 'Boarding' },
  { value: 'reference', label: 'Reference' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All Statuses' },
  ...ALL_HORSE_STATUSES,
];

const HorsesPage = () => {
  const notify = useNotify();
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterOwnerType, setFilterOwnerType] = useState('owned');
  const [filterStatus, setFilterStatus] = useState('current');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('horses_sortBy') || 'name');
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('horses_sortOrder') || 'asc');
  const navigate = useNavigate();
  const { openHorseModal } = useHorseModal();

  // Reference data states (for filters)
  const [horseTypes, setHorseTypes] = useState([]);

  // "Add to income" posts under the Horse: Sales income type. It is created through the
  // reference UI rather than seeded, so it is resolved by name at load and the menu item
  // stays disabled if it is missing rather than posting to an arbitrary type id.
  const [salesIncomeType, setSalesIncomeType] = useState(null);
  const [horsesWithIncome, setHorsesWithIncome] = useState(() => new Set());

  // "Add to expense" is the purchase-side mirror of "Add to income": posts under the
  // Horse: Purchase expense type, also created through the reference UI rather than
  // seeded.
  const [purchaseExpenseType, setPurchaseExpenseType] = useState(null);
  const [horsesWithExpense, setHorsesWithExpense] = useState(() => new Set());

  // Load reference data for filters
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      const typesRes = await referenceAPI.getHorseTypes();
      setHorseTypes(toArray(typesRes.data, 'horse types'));
    } catch (error) {
      console.error('Error loading reference data:', error);
    }

    try {
      const incomeTypesRes = await referenceAPI.getIncomeTypes();
      const salesType = toArray(incomeTypesRes.data, 'income types').find(
        (type) => type.category?.toLowerCase() === 'horse' && type.name?.toLowerCase() === 'sales'
      );
      setSalesIncomeType(salesType ?? null);
      if (salesType) {
        // Which horses are already on the books. Scoped to the Horse category so this
        // does not pull the whole income table as it grows.
        const incomeRes = await incomeAPI.getAll({ category_id: salesType.category_id });
        setHorsesWithIncome(new Set(
          toArray(incomeRes.data, 'income')
            .filter((row) => row.income_type_id === salesType.id && row.horse_id)
            .map((row) => row.horse_id)
        ));
      }
    } catch (error) {
      // Non-fatal: the page still lists horses, "Add to income" just stays disabled.
      console.error('Error loading income reference data:', error);
    }

    try {
      const expenseTypesRes = await referenceAPI.getExpenseTypes();
      const purchaseType = toArray(expenseTypesRes.data, 'expense types').find(
        (type) => type.category?.toLowerCase() === 'horse' && type.name?.toLowerCase() === 'purchase'
      );
      setPurchaseExpenseType(purchaseType ?? null);
      if (purchaseType) {
        const expenseRes = await expensesAPI.getAll({ category_id: purchaseType.category_id });
        setHorsesWithExpense(new Set(
          toArray(expenseRes.data, 'expenses')
            .filter((row) => row.expense_type_id === purchaseType.id && row.horse_id)
            .map((row) => row.horse_id)
        ));
      }
    } catch (error) {
      // Non-fatal: the page still lists horses, "Add to expense" just stays disabled.
      console.error('Error loading expense reference data:', error);
    }
  };

  const getHorseDisplayName = (horse) => {
    if (!horse) return '';
    const registeredName = horse.registered_name?.trim();
    const barnName = horse.name?.trim();
    if (registeredName) return registeredName;
    if (barnName) return barnName;
    return `Horse #${horse.id}`;
  };

  const loadHorses = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const params = {};
      if (filterType) params.type = filterType;
      if (filterOwnerType) params.owner_type = filterOwnerType;
      if (filterStatus) params.status = filterStatus;
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
      
      const response = await horsesAPI.getAll(params);
      setHorses(toArray(response.data, 'horses'));
    } catch (error) {
      console.error('Error loading horses:', error);
      setHorses([]);
      const message = error?.response?.data?.error || 'Unable to load horses for the selected filters. Please try again.';
      setLoadError(message);
    } finally {
      setLoading(false);
    }
  }, [filterType, filterOwnerType, filterStatus, sortBy, sortOrder]);

  useEffect(() => {
    loadHorses();
  }, [loadHorses]);

  useRefreshOnFocus(loadHorses);

  const handleOpenDialog = (horse = null) => {
    openHorseModal(horse, () => {
      loadHorses();
    });
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [allbreedTarget, setAllbreedTarget] = useState(null);
  const [incomeTarget, setIncomeTarget] = useState(null);
  const [saleAgreementTarget, setSaleAgreementTarget] = useState(null);
  const [expenseTarget, setExpenseTarget] = useState(null);
  const [pedigreeTarget, setPedigreeTarget] = useState(null);

  // The only fields safe to patch onto a `horses` row from an allbreed
  // response. `eligibility()`'s payload (and the patch AllBreedSubmitDialog
  // reports on success) carries a `name` that means registered-name-preferred
  // display name — spreading it wholesale would clobber this table's `name`,
  // which means the barn name.
  const ALLBREED_PATCH_FIELDS = ['allbreed_status', 'allbreed_horse_id', 'allbreed_last_error'];
  const pickAllbreedFields = (source) => {
    const patch = {};
    ALLBREED_PATCH_FIELDS.forEach((field) => {
      if (field in source) patch[field] = source[field];
    });
    return patch;
  };

  // Patch the row locally the instant a submit/match lands an allbreed_horse_id,
  // so the "Submit to AllBreed" menu item disables immediately — loadHorses()'s
  // network round-trip alone leaves a window where the item is still clickable
  // for an already-linked horse.
  const handleAllbreedComplete = (patch) => {
    if (patch?.allbreed_horse_id) {
      const safePatch = pickAllbreedFields(patch);
      setHorses((prev) =>
        prev.map((h) => (h.id === allbreedTarget?.id ? { ...h, ...safePatch } : h))
      );
    }
    loadHorses();
  };

  // Belt-and-suspenders for the same staleness the patch above targets: if
  // "Submit to AllBreed" still got opened for an already-linked horse (stale
  // menu, another tab, whatever), the dialog's own live eligibility check
  // catches it and calls this instead of showing a dead-end blocked screen.
  const handleAlreadyLinked = (horse) => {
    setHorses((prev) =>
      prev.map((h) => (h.id === horse.id ? { ...h, ...pickAllbreedFields(horse) } : h))
    );
    notify.info(
      `${horse.registered_name || horse.name} is already on AllBreed as ${horse.allbreed_horse_id}.`
    );
  };

  // Why "Add to income" is unavailable for this horse, or null when it is available.
  // Doubles as the disabled flag and the tooltip text on the menu item.
  const incomeBlockReason = (horse) => {
    if (horsesWithIncome.has(horse.id)) return 'Already added to income';
    if (!salesIncomeType) return "Income type 'Horse: Sales' is not configured";
    if (!horse.date_sale_paid) return 'A Sale Paid date is required to record income';
    if (horse.sale_amount === null || horse.sale_amount === undefined || horse.sale_amount === '') {
      return 'A Sale Amount is required to record income';
    }
    return null;
  };

  const handleAddIncomeConfirm = async () => {
    const horse = incomeTarget;
    if (!horse || !salesIncomeType) return;
    try {
      // category_id is derived server-side from the income type; income_location_id is
      // omitted because no income locations exist.
      await incomeAPI.create({
        income_date: horse.date_sale_paid,
        amount: Number(horse.sale_amount),
        income_type_id: salesIncomeType.id,
        horse_id: horse.id,
        summary: buildSaleIncomeSummary(horse),
      });
      setHorsesWithIncome((prev) => new Set(prev).add(horse.id));
      setIncomeTarget(null);
    } catch (error) {
      console.error('Error adding horse to income:', error);
      setIncomeTarget(null);
      setLoadError(error.response?.data?.error || 'Error adding this horse to income');
    }
  };

  // Why "Add to expense" is unavailable for this horse, or null when it is available.
  const expenseBlockReason = (horse) => {
    if (horsesWithExpense.has(horse.id)) return 'Already added to expenses';
    if (!purchaseExpenseType) return "Expense type 'Horse: Purchase' is not configured";
    if (!horse.date_purchase_paid) return 'A Purchase Paid date is required to record an expense';
    if (horse.purchase_amount === null || horse.purchase_amount === undefined || horse.purchase_amount === '') {
      return 'A Purchase Amount is required to record an expense';
    }
    return null;
  };

  const handleAddExpenseConfirm = async () => {
    const horse = expenseTarget;
    if (!horse || !purchaseExpenseType) return;
    try {
      // category_id is derived server-side from the expense type.
      await expensesAPI.create({
        expense_date: horse.date_purchase_paid,
        amount: Number(horse.purchase_amount),
        expense_type_id: purchaseExpenseType.id,
        horse_id: horse.id,
        summary: buildPurchaseExpenseSummary(horse),
      });
      setHorsesWithExpense((prev) => new Set(prev).add(horse.id));
      setExpenseTarget(null);
    } catch (error) {
      console.error('Error adding horse to expenses:', error);
      setExpenseTarget(null);
      setLoadError(error.response?.data?.error || 'Error adding this horse to expenses');
    }
  };

  const handleDeleteClick = (id) => setDeleteTarget(id);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await horsesAPI.delete(deleteTarget);
      setDeleteTarget(null);
      loadHorses();
    } catch (error) {
      console.error('Error deleting horse:', error);
      notify.error(errorMessage(error, 'Error deleting horse'));
    }
  };


  const handleSort = (column) => {
    if (sortBy === column) {
      const next = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(next);
      localStorage.setItem('horses_sortOrder', next);
    } else {
      setSortBy(column);
      setSortOrder('asc');
      localStorage.setItem('horses_sortBy', column);
      localStorage.setItem('horses_sortOrder', 'asc');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'for_sale':
        return 'primary';
      case 'sale_pending':
        return 'warning';
      case 'retired':
        return 'secondary';
      case 'sold':
        return 'info';
      case 'deceased':
        return 'error';
      case 'lease_terminated':
        return 'warning';
      default:
        return 'default';
    }
  };

  const displayDate = (value) => formatDate(value) ?? '-';
  const displayDateTime = (value) => formatUtcDateTime(value) ?? '-';

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(numeric)) {
      return value;
    }
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(numeric);
  };

  // Sale Amount lives ~27 columns into the horizontally-scrolling region, so the sum is not
  // something you can eyeball. Blanks are counted rather than folded in as zero — a For Sale
  // horse with no asking price entered is exactly what makes a bare total misleading.
  const saleTotal = useMemo(() => {
    const label = SALE_TOTAL_LABELS[filterStatus];
    if (!label) {
      return null;
    }
    let total = 0;
    let missing = 0;
    let balanceDue = 0;
    horses.forEach((horse) => {
      const amount = Number(horse.sale_amount);
      if (horse.sale_amount === null || horse.sale_amount === undefined
        || horse.sale_amount === '' || Number.isNaN(amount)) {
        missing += 1;
      } else {
        total += amount;
        balanceDue += Number(horse.balance_due_sale) || 0;
      }
    });
    return { label, total, missing, balanceDue, count: horses.length };
  }, [horses, filterStatus]);

  const typeFilterOptions = useMemo(() => [
    { value: '', label: 'All Types' },
    ...horseTypes
      .filter((type) => Boolean(type?.name))
      .map((type) => ({ value: type.name.toLowerCase(), label: type.name })),
  ], [horseTypes]);

  const safeValue = (value, fallback = '-') => {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return value;
  };

  const stickyBackground = 'background.paper';
  const horsesFilterAutocompleteComponentsProps = {
    paper: {
      sx: {
        zIndex: (theme) => theme.zIndex.modal + 1,
      },
    },
  };

  const getHeaderCellSx = (column) => {
    const stickyStyles = column.sticky
      ? {
          position: 'sticky',
          left: column.left ?? 0,
          zIndex: 8, // Keep sticky header columns above sticky body columns
          backgroundColor: stickyBackground,
        }
      : {};

    return {
      minWidth: column.minWidth || 160,
      maxWidth: column.maxWidth,
      whiteSpace: column.wrap ? 'normal' : 'nowrap',
      backgroundColor: stickyBackground,
      zIndex: column.sticky ? 8 : 7, // Keep all header cells above body cells while scrolling
      ...stickyStyles,
      ...(column.headerSx || {}),
    };
  };

  const getBodyCellSx = (column) => {
    const stickyStyles = column.sticky
      ? {
          position: 'sticky',
          left: column.left ?? 0,
          zIndex: 4,
          backgroundColor: stickyBackground,
        }
      : {};

    return {
      minWidth: column.minWidth || 160,
      maxWidth: column.maxWidth,
      whiteSpace: column.wrap ? 'normal' : 'nowrap',
      ...(column.wrap ? { wordBreak: 'break-word' } : {}),
      ...stickyStyles,
      ...(column.bodySx || {}),
    };
  };

  const columns = [
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      sticky: true,
      left: 0,
      minWidth: ACTIONS_COLUMN_WIDTH,
      align: 'center',
      headerSx: actionsKabobHeaderSx,
      bodySx: actionsKabobCellSx,
      render: (horse) => (
        <RowActionsMenu
          actions={[
            { key: 'view', label: 'View', icon: <VisibilityIcon fontSize="small" />, onClick: () => navigate(`/horses/${horse.id}`) },
            { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(horse) },
            {
              key: 'pedigree',
              label: 'View Pedigree',
              icon: <AccountTreeOutlinedIcon fontSize="small" />,
              onClick: () => setPedigreeTarget(horse),
            },
            {
              key: 'allbreed',
              label: 'Submit to AllBreed',
              icon: <PublicIcon fontSize="small" />,
              onClick: () => setAllbreedTarget(horse),
              disabled: Boolean(horse.allbreed_horse_id),
              title: horse.allbreed_horse_id
                ? `Already on AllBreed (${horse.allbreed_horse_id})`
                : undefined,
            },
            horse.current_status === 'sold' && {
              key: 'add-income',
              label: 'Add to income',
              icon: <PaidIcon fontSize="small" />,
              onClick: () => setIncomeTarget(horse),
              disabled: Boolean(incomeBlockReason(horse)),
              title: incomeBlockReason(horse) || undefined,
            },
            // The contract is signed before the horse is paid off, so sale_pending counts too.
            ['sale_pending', 'sold'].includes(horse.current_status) && {
              key: 'sale-agreement',
              label: 'Sales Agreement',
              icon: <DescriptionIcon fontSize="small" />,
              onClick: () => setSaleAgreementTarget(horse),
            },
            Boolean(horse.date_purchase_paid) && {
              key: 'add-expense',
              label: 'Add to expenses',
              icon: <ReceiptLongIcon fontSize="small" />,
              onClick: () => setExpenseTarget(horse),
              disabled: Boolean(expenseBlockReason(horse)),
              title: expenseBlockReason(horse) || undefined,
            },
            { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(horse.id), color: 'error' },
          ]}
        />
      ),
    },
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      sticky: true,
      left: ACTIONS_COLUMN_WIDTH,
      minWidth: NAME_COLUMN_WIDTH,
      maxWidth: NAME_COLUMN_WIDTH,
      headerSx: { pl: 1 },
      bodySx: { pl: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
      render: (horse) => safeValue(horse.name),
    },
    {
      id: 'date_of_birth',
      label: 'Foaled',
      sortable: true,
      minWidth: FOALED_COLUMN_WIDTH,
      render: (horse) => displayDate(horse.date_of_birth),
    },
    {
      id: 'registered_name',
      label: 'Registered Name',
      sortable: true,
      minWidth: 240,
      render: (horse) => safeValue(horse.registered_name),
    },
    {
      id: 'type',
      label: 'Type',
      sortable: true,
      minWidth: 160,
      render: (horse) =>
        horse.type ? <Chip label={horse.type} size="small" /> : '-',
    },
    {
      id: 'current_status',
      label: 'Status',
      sortable: true,
      minWidth: 160,
      render: (horse) =>
        horse.current_status ? (
          <Chip
            label={formatHorseCurrentStatus(horse.current_status)}
            size="small"
            color={getStatusColor(horse.current_status)}
          />
        ) : (
          '-'
        ),
    },
    {
      id: 'breed',
      label: 'Breed',
      sortable: true,
      minWidth: 180,
      render: (horse) => safeValue(horse.breed),
    },
    {
      id: 'color',
      label: 'Color',
      sortable: true,
      minWidth: 160,
      render: (horse) => safeValue(horse.color),
    },
    {
      id: 'age',
      label: 'Age',
      sortable: false,
      minWidth: 100,
      render: (horse) => calculateHorseAge(horse.date_of_birth),
    },
    {
      id: 'microchip',
      label: 'Microchip',
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.microchip),
    },
    {
      id: 'owner_type',
      label: 'Owner Type',
      sortable: true,
      minWidth: 160,
      render: (horse) =>
        horse.owner_type ? <Chip label={horse.owner_type} size="small" /> : '-',
    },
    {
      id: 'location',
      label: 'Location',
      sortable: true,
      minWidth: 220,
      render: (horse) => safeValue(horse.location),
    },
    {
      id: 'sire',
      label: 'Sire',
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.sire_name || (horse.sire_horse ? getHorseDisplayName(horse.sire_horse) : null)),
    },
    {
      id: 'dam',
      label: 'Dam',
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.dam_name || (horse.dam_horse ? getHorseDisplayName(horse.dam_horse) : null)),
    },
    {
      id: 'sire_sire',
      label: "Sire's Sire",
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.sire_sire_name || (horse.sire_sire_horse ? getHorseDisplayName(horse.sire_sire_horse) : null)),
    },
    {
      id: 'dam_sire',
      label: "Dam's Sire",
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.dam_sire_name || (horse.dam_sire_horse ? getHorseDisplayName(horse.dam_sire_horse) : null)),
    },
    {
      id: 'height',
      label: 'Height (hands)',
      sortable: true,
      minWidth: 160,
      render: (horse) => safeValue(horse.height),
    },
    {
      id: 'weight',
      label: 'Weight (lbs)',
      sortable: true,
      minWidth: 160,
      render: (horse) => safeValue(horse.weight),
    },
    {
      id: 'blanket_size',
      label: 'Blanket Size',
      sortable: true,
      minWidth: 160,
      render: (horse) => safeValue(horse.blanket_size),
    },
    {
      id: 'color_panel',
      label: 'Color Panel',
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.color_panel),
    },
    {
      id: 'dna_test',
      label: 'DNA Test',
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.dna_test),
    },
    {
      id: 'disease_panel',
      label: 'Disease Panel',
      sortable: true,
      minWidth: 200,
      render: (horse) => safeValue(horse.disease_panel),
    },
    {
      id: 'date_purchased',
      label: 'Date Purchased',
      sortable: true,
      minWidth: 180,
      render: (horse) => displayDate(horse.date_purchased),
    },
    {
      id: 'date_purchase_paid',
      label: 'Purchase Paid',
      sortable: true,
      minWidth: 180,
      render: (horse) => displayDate(horse.date_purchase_paid),
    },
    {
      id: 'date_sold',
      label: 'Date Sold',
      sortable: true,
      minWidth: 180,
      render: (horse) => displayDate(horse.date_sold),
    },
    {
      id: 'date_sale_paid',
      label: 'Sale Paid',
      sortable: true,
      minWidth: 180,
      render: (horse) => displayDate(horse.date_sale_paid),
    },
    {
      id: 'purchase_amount',
      label: 'Purchase Amount',
      sortable: true,
      minWidth: 180,
      render: (horse) => formatCurrency(horse.purchase_amount),
    },
    {
      id: 'balance_due_purchase',
      label: 'Balance Due (Purchase)',
      sortable: false,
      minWidth: 180,
      render: (horse) => (horse.purchase_amount === null || horse.purchase_amount === undefined
        ? '-' : formatCurrency(horse.balance_due_purchase)),
    },
    {
      id: 'sale_amount',
      label: 'Sale Amount',
      sortable: true,
      minWidth: 180,
      render: (horse) => formatCurrency(horse.sale_amount),
    },
    {
      id: 'balance_due_sale',
      label: 'Balance Due (Sale)',
      sortable: false,
      minWidth: 180,
      render: (horse) => (horse.sale_amount === null || horse.sale_amount === undefined
        ? '-' : formatCurrency(horse.balance_due_sale)),
    },
    {
      id: 'gestation_days_override',
      label: 'Gestation Override',
      sortable: true,
      minWidth: 180,
      render: (horse) => horse.gestation_days_override != null ? horse.gestation_days_override : '-',
    },
    {
      id: 'allbreed_horse_id',
      label: 'AllBreed ID',
      sortable: true,
      minWidth: 200,
      render: (horse) => horse.allbreed_horse_id
        ? <a href={`https://beta.allbreedpedigree.com/${horse.allbreed_horse_id}`} target="_blank" rel="noreferrer">{horse.allbreed_horse_id}</a>
        : '-',
    },
    {
      id: 'allbreed_status',
      label: 'AllBreed Status',
      sortable: true,
      minWidth: 160,
      render: (horse) => horse.allbreed_status
        ? <span style={{ textTransform: 'capitalize' }}>{horse.allbreed_status}</span>
        : '-',
    },
    {
      id: 'allbreed_submitted_at',
      label: 'AllBreed Submitted',
      sortable: true,
      minWidth: 180,
      render: (horse) => displayDate(horse.allbreed_submitted_at),
    },
    {
      id: 'created_by',
      label: 'Created By',
      sortable: true,
      minWidth: 160,
      render: (horse) => safeValue(horse.created_by),
    },
    {
      id: 'updated_by',
      label: 'Updated By',
      sortable: true,
      minWidth: 160,
      render: (horse) => safeValue(horse.updated_by),
    },
    {
      id: 'trainer',
      label: 'Trainer',
      sortable: true,
      minWidth: 180,
      render: (horse) => safeValue(horse.trainer_name || horse.trainer_id),
    },
    {
      id: 'created_at',
      label: 'Created At',
      sortable: true,
      minWidth: 200,
      render: (horse) => displayDateTime(horse.created_at),
    },
    {
      id: 'updated_at',
      label: 'Updated At',
      sortable: true,
      minWidth: 200,
      render: (horse) => displayDateTime(horse.updated_at),
    },
  ];


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            alignItems: 'center',
            gap: { xs: 1.5, sm: 0 },
            mb: 1,
            pt: 0.5,
          }}
        >
          <Typography variant="h4">Horses{loading && horses.length === 0 ? '' : ` (${horses.length})`}</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              minWidth: { xs: 0, sm: 'auto' },
              width: { xs: 32, sm: 'auto' },
              height: { xs: 32, sm: 'auto' },
              px: { xs: 0, sm: 2 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              Add Horse
            </Box>
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap', mb: 1.5, alignItems: 'center' }}>
          <Autocomplete
            id="horses-filter-type"
            size="small"
            disableClearable
            sx={{ minWidth: { xs: 105, sm: 150 } }}
            options={typeFilterOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            filterOptions={createPrefixFilter((option) => option.label)}
            componentsProps={horsesFilterAutocompleteComponentsProps}
            value={typeFilterOptions.find((option) => option.value === filterType) ?? typeFilterOptions[0]}
            onChange={(e, newValue) => setFilterType(newValue?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Type" />}
          />

          <Autocomplete
            id="horses-filter-owner-type"
            size="small"
            disableClearable
            sx={{ minWidth: { xs: 105, sm: 150 } }}
            options={OWNER_TYPE_FILTER_OPTIONS}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            filterOptions={createPrefixFilter((option) => option.label)}
            componentsProps={horsesFilterAutocompleteComponentsProps}
            value={OWNER_TYPE_FILTER_OPTIONS.find((option) => option.value === filterOwnerType) ?? OWNER_TYPE_FILTER_OPTIONS[0]}
            onChange={(e, newValue) => setFilterOwnerType(newValue?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Owner Type" />}
          />

          <Autocomplete
            id="horses-filter-status"
            size="small"
            disableClearable
            sx={{ minWidth: { xs: 105, sm: 150 } }}
            options={STATUS_FILTER_OPTIONS}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            filterOptions={createPrefixFilter((option) => option.label)}
            componentsProps={horsesFilterAutocompleteComponentsProps}
            value={STATUS_FILTER_OPTIONS.find((option) => option.value === filterStatus) ?? STATUS_FILTER_OPTIONS[0]}
            onChange={(e, newValue) => setFilterStatus(newValue?.value ?? '')}
            renderInput={(params) => <TextField {...params} label="Status" />}
          />

          {saleTotal && (
            <Box
              aria-live="polite"
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 1,
                flexWrap: 'wrap',
                ml: { sm: 'auto' },
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                {saleTotal.label}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {formatCurrency(saleTotal.total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {saleTotal.count} {saleTotal.count === 1 ? 'horse' : 'horses'}
                {saleTotal.missing > 0 ? ` · ${saleTotal.missing} with no amount` : ''}
              </Typography>
              {filterStatus === 'sale_pending' && saleTotal.balanceDue > 0 && (
                <Typography variant="caption" color="text.secondary">
                  · {formatCurrency(saleTotal.balanceDue)} still owed
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {filterStatus === 'current' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexWrap: 'wrap',
              mb: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Current — status key
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <Chip label="Active" size="small" color="success" />
              <Chip label="For Sale" size="small" color="primary" />
              <Chip label="Sale Pending" size="small" color="warning" />
              <Chip label="Retired" size="small" color="secondary" />
            </Stack>
          </Box>
        )}

        {loadError ? (
          <Alert severity="error" sx={{ mb: 0.5 }}>
            {loadError}
          </Alert>
        ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.id} align={column.align} sx={getHeaderCellSx(column)}>
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortBy === column.id}
                      direction={sortBy === column.id ? sortOrder : 'asc'}
                      onClick={() => handleSort(column.id)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody sx={{ opacity: loading && horses.length > 0 ? 0.5 : 1, transition: 'opacity 150ms ease' }}>
            {loading && horses.length === 0 && <TableSkeletonRows columns={columns.length} />}
            {horses.map((horse) => (
              <TableRow key={horse.id} hover>
                {columns.map((column) => (
                  <TableCell key={column.id} align={column.align} sx={getBodyCellSx(column)}>
                    {column.render ? column.render(horse) : safeValue(horse[column.id])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Horse"
        message="Permanently delete this horse? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />

      {/* The generated summary is not editable from this page, so it is shown before the
          record is written. Spans rather than block elements — ConfirmDialog wraps the
          message in a Typography, which renders as a <p>. */}
      <ConfirmDialog
        open={Boolean(incomeTarget)}
        title="Add to Income"
        message={incomeTarget && (
          <>
            <Box component="span" sx={{ display: 'block' }}>
              {`Record ${formatSaleAmount(incomeTarget.sale_amount)} of income under Horse: Sales, dated ${displayDate(incomeTarget.date_sale_paid)}.`}
            </Box>
            <Box component="span" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
              {buildSaleIncomeSummary(incomeTarget)}
            </Box>
          </>
        )}
        confirmLabel="Add to Income"
        confirmColor="primary"
        onConfirm={handleAddIncomeConfirm}
        onCancel={() => setIncomeTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(expenseTarget)}
        title="Add to Expenses"
        message={expenseTarget && (
          <>
            <Box component="span" sx={{ display: 'block' }}>
              {`Record ${formatSaleAmount(expenseTarget.purchase_amount)} of expense under Horse: Purchase, dated ${displayDate(expenseTarget.date_purchase_paid)}.`}
            </Box>
            <Box component="span" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
              {buildPurchaseExpenseSummary(expenseTarget)}
            </Box>
          </>
        )}
        confirmLabel="Add to Expenses"
        confirmColor="primary"
        onConfirm={handleAddExpenseConfirm}
        onCancel={() => setExpenseTarget(null)}
      />

      {saleAgreementTarget && (
        <SaleAgreementDialog
          horse={saleAgreementTarget}
          onClose={() => setSaleAgreementTarget(null)}
        />
      )}

      {allbreedTarget && (
        <AllBreedSubmitDialog
          open
          horse={allbreedTarget}
          onClose={() => setAllbreedTarget(null)}
          onComplete={handleAllbreedComplete}
          onAlreadyLinked={handleAlreadyLinked}
        />
      )}

      {pedigreeTarget && (
        <PedigreeModal
          open
          onClose={() => setPedigreeTarget(null)}
          horseId={pedigreeTarget.id}
          horseName={pedigreeTarget.name || pedigreeTarget.registered_name}
        />
      )}
    </Box>
  );
};

export default HorsesPage;

